/**
 * @Copyright 2014-2019 DataVoke, Inc. All rights reserved.
 */

'use strict';

import {tempID} from "../utils/tools";
import {
    applyUnknownProperties,
    ClientProperties,
    EntityBase,
    EntityDefinitionBase,
    setPropertyOrDefault
} from "./entitybase";
import {
    AggregateTypes,
    EntityTypes,
    PropertyDataType
} from "../utils/dvtypes";

export class PropertyDefinition extends EntityDefinitionBase {
    /**
     * Create a new Property Definition instance
     * @param {Object|Null} [defaults = {}] - (OPTIONAL) Default object (transmitted definition object?) to use for properties
     * @param {boolean} [defaults.AggregateType] - Aggregate type for summaries of columns based on this property
     * @param {boolean} [defaults.AllowNulls] - Flag to indicate if NULL values are allowed in the property
     * @param {EntityLinker} [defaults.AutoLinkRecordTypeID] - Optional Entity Linker property to indicate that this is linked to the record of another table
     * @param {string} [defaults.Comment] - Optional notes for this entity
     * @param {PropertyDataType} [defaults.DataType] - The type of property this entity represents
     * @param {string} [defaults.DefaultValue] - Expression for default value
     * @param {string} [defaults.DisplayFormat] - Display format string
     * @param {boolean} [defaults.EnforceList] - Flag to indicate if only items in the drop down or list can be in the field.
     * @param {boolean} [defaults.ERESDate] - Flag to indicate if the service should generate the field for this value
     * @param {string} [defaults.Formula] - Optional expression formula for the value of this property
     * @param {string} [defaults.InternalDesc] - Internal description for this property
     * @param {boolean} [defaults.IsAutoLinkRefOnly] - Flag to indicate if this value is sourced from the value from a linked record (see AutoLinkRecordTypeID)
     * @param {boolean} [defaults.IsCalculateExpressionOnlyOnceEnabled] - Flag to indicate if the value expression is only to be run once when value is created.
     * @param {boolean} [defaults.IsHidden] - Flag to indicate if this property should be hidden in the UI
     * @param {boolean} [defaults.IsIgnoreRecordStatus] - Flag to indicate if this value *should NOT* have the record status applied to it.
     * @param {boolean} [defaults.IsRequired] - Flag to indicate if this value is required for saving (mutually exclusive with NotSaved)
     * @param {boolean} [defaults.IsSpellCheckEnabled] - Flag to indicate if this property should be spell-checked
     * @param {boolean} [defaults.IsSynched] - Flag to indicate if this value is synced to its drop down source; when it changes, if this is the same value it should also change.
     * @param {string} [defaults.ListExpression] - Expression to generate source information for a List datatype property.
     * @param {string} [defaults.Mask] - Optional string to indicate the Mask character
     * @param {boolean} [defaults.NotSaved] - Flag to indicate if property is never saved (mutually exclusive with IsRequired)
     * @param {EntityLinker} [defaults.PivotForPropertyID] - Optional property linker for using pivot properties
     * @param {EntityLinker} [defaults.RefPropertyID] - Property linker for analysis operations
     * @param {EntityLinker} [defaults.RefViewID] - View linker for analysis operations
     * @param {Array} [defaults.StatusExpressions] - Array of status expressions that return a linker for a valueStatus if the expression condition is met.
     */
    constructor (defaults = {}) {
        if (defaults == null) {
            // this can happen if "null" is actually passed as "defaults"
            defaults = {};
        }

        super(defaults);

        /**
         * Name for this property definition
         * @type {string}
         */
        this.Name = setPropertyOrDefault(this.Name, "New Property");

        /**
         * Aggregate type for summaries of columns based on this property
         * @type {AggregateTypes|number}
         */
        this.AggregateType = setPropertyOrDefault(defaults.AggregateType, AggregateTypes.Count);

        /**
         * Flag to indicate if NULL values are allowed in the property
         * @type {boolean}
         */
        this.AllowNulls = setPropertyOrDefault(defaults.AllowNulls, false);

        /**
         * Optional Entity Linker property to indicate that this is linked to the record of another table
         * @type {EntityLinker}
         */
        this.AutoLinkRecordTypeID = setPropertyOrDefault(defaults.AutoLinkRecordTypeID);

        /**
         * Optional notes for this entity
         * @type {string|Null}
         */
        this.Comment = setPropertyOrDefault(defaults.Comment, undefined);

        /**
         * The type of property this entity represents
         * @type {PropertyDataType|number}
         */
        this.DataType = setPropertyOrDefault(defaults.DataType, PropertyDataType.Text);

        /**
         * Optional expression for default value
         * @type {string}
         */
        this.DefaultValue = setPropertyOrDefault(defaults.DefaultValue, "");

        /**
         * Optional display format string
         * @type {string}
         */
        this.DisplayFormat = setPropertyOrDefault(defaults.DisplayFormat, "");

        /**
         * Flag to indicate if only items in the drop down or list can be in the field.
         * @type {boolean}
         */
        this.EnforceList = setPropertyOrDefault(defaults.EnforceList, false);

        /**
         * Flag to indicate if the service should generate the field for this value
         * @type {boolean}
         */
        this.ERESDate = setPropertyOrDefault(defaults.ERESDate, false);

        /**
         * Optional expression formula for the value of this property
         * @type {string|Null}
         */
        this.Formula = setPropertyOrDefault(defaults.Formula);

        /**
         * Optional internal description for this property
         * @type {string|Null}
         */
        this.InternalDesc = setPropertyOrDefault(defaults.InternalDesc);

        /**
         * Flag to indicate if this value is sourced from the value from a linked record (see AutoLinkRecordTypeID)
         * @type {boolean}
         */
        this.IsAutoLinkRefOnly = setPropertyOrDefault(defaults.IsAutoLinkRefOnly, false);

        /**
         * Flag to indicate if the value expression is only to be run once when value is created.
         * @type {boolean}
         */
        this.IsCalculateExpressionOnlyOnceEnabled = setPropertyOrDefault(defaults.IsCalculateExpressionOnlyOnceEnabled, false);

        /**
         * Flag to indicate if this property should be hidden in the UI
         * @type {boolean}
         */
        this.IsHidden = setPropertyOrDefault(defaults.IsHidden, false);

        /**
         * Flag to indicate if this value *should NOT* have the record status applied to it.
         * @type {boolean}
         */
        this.IsIgnoreRecordStatus = setPropertyOrDefault(defaults.IsIgnoreRecordStatus, false);

        /**
         * Flag to indicate if this value is required for saving (mutually exclusive with NotSaved)
         * @type {boolean}
         */
        this.IsRequired = setPropertyOrDefault(defaults.IsRequired, false);

        /**
         * Flag to indicate if this property should be spell-checked
         * @type {boolean}
         */
        this.IsSpellCheckEnabled = setPropertyOrDefault(defaults.IsSpellCheckEnabled, false);

        /**
         * Flag to indicate if this value is synced to its drop down source; when it changes, if this is the same value it should also change.
         * @type {boolean}
         */
        this.IsSynched = setPropertyOrDefault(defaults.IsSynched, true);

        /**
         * Optional expression to generate source information for a List datatype property.
         * @type {string|Null}
         */
        this.ListExpression = setPropertyOrDefault(defaults.ListExpression);

        /**
         * Optional string to indicate the Mask character
         * @type {string|Null}
         */
        this.Mask = setPropertyOrDefault(defaults.Mask);

        /**
         * Flag to indicate if property is never saved (mutually exclusive with IsRequired)
         * @type {boolean}
         */
        this.NotSaved = setPropertyOrDefault(defaults.NotSaved, false);

        /**
         * Optional property linker for using pivot properties
         * @type {EntityLinker|null}
         */
        this.PivotForPropertyID = setPropertyOrDefault(defaults.PivotForPropertyID);

        /**
         * Optional property linker for analysis operations
         * @type {EntityLinker|null}
         */
        this.RefPropertyID = setPropertyOrDefault(defaults.RefPropertyID);

        /**
         * Optional view linker for analysis operations
         * @type {EntityLinker|null}
         */
        this.RefViewID = setPropertyOrDefault(defaults.RefViewID);

        /**
         * Array of status expressions that return a linker for a valueStatus if the expression condition is met.
         * @type {Array}
         */
        this.StatusExpressions = setPropertyOrDefault(defaults.StatusExpressions, []);

        // allow for any entity definition properties not known by the class
        applyUnknownProperties(this, defaults);
    }
}

export class PropertyEntity extends EntityBase {
    /**
     * Create a new Property entity instance
     * @param {Object|Null} [defaults = {}] - (OPTIONAL) Default object (transmitted definition object?) to use for properties
     * @param {number} [defaults.PropertyID] - Optional property ID number for this property entity
     * @param {string} [defaults.CategoryUID] - Optional UID of the category entity for this property entity
     * @param {Object} [defaults.Definition] - Default object for properties of the property entity definition
     */
    constructor (defaults = {}) {
        if (defaults == null) {
            // this can happen if "null" is actually passed as "defaults"
            defaults = {};
        }

        // ensure that the definition property of the defaults is an object.
        if (defaults.Definition && typeof defaults.Definition === 'string') {
            defaults.Definition = JSON.parse(defaults.Definition);
        }

        super(defaults);

        /**
         * Name for this property entity
         * @type {string}
         */
        this.Name = setPropertyOrDefault(this.Name, "New_Property");

        /**
         * Retrieve or generate the property ID value for this property entity
         * @type {number}
         */
        this.PropertyID = setPropertyOrDefault(defaults.PropertyID, tempID);

        /**
         * The UID value of the category entity this property entity belongs to
         * @type {string|null}
         */
        this.CategoryUID = setPropertyOrDefault(defaults.CategoryUID, null);

        /**
         * Set the definition for this property entity
         * @type {PropertyDefinition}
         */
        this.Definition = new PropertyDefinition(defaults.Definition);

        /**
         * Set the client properties for this entity type
         * @type {ClientProperties}
         */
        this.clientProperties = new ClientProperties(EntityTypes.Property);
    }
}
